import type {
  DraftWarning,
  ImportConfirmRequest,
  ImportJob,
  RecipeWriteInput,
  UserProfile,
} from '@recipes/contracts';
import { draftToRecipeInput, findPreferenceConflicts } from '@recipes/domain';
import { Checkbox } from '@recipes/ui';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DraftReviewForm } from './draft-review-form';
import { DuplicatesPanel, SourceBox, WarningsList, type DuplicateChoice } from './review-parts';

export interface ImportReviewScreenProps {
  job: ImportJob;
  profile?: Pick<
    UserProfile,
    'dietaryPreferences' | 'avoidedIngredients' | 'defaultServings'
  > | null;
  confirming?: boolean;
  onConfirm: (request: ImportConfirmRequest) => void | Promise<void>;
}

/**
 * Review step of the URL import: source attribution, warnings, duplicates, image rights
 * and the editable draft. Nothing is persisted until the user confirms.
 */
export function ImportReviewScreen({
  job,
  profile,
  confirming,
  onConfirm,
}: ImportReviewScreenProps) {
  const { t } = useTranslation();
  const draft = job.draft;
  const [choice, setChoice] = useState<DuplicateChoice>({
    resolution: 'SAVE_NEW',
    existingRecipeId: null,
  });
  const [copyImage, setCopyImage] = useState(false);

  const initial = useMemo(
    () =>
      draft
        ? draftToRecipeInput(draft, {
            defaultServings: profile?.defaultServings ? String(profile.defaultServings) : undefined,
          })
        : null,
    [draft, profile?.defaultServings],
  );

  const warnings = useMemo<DraftWarning[]>(() => {
    if (!draft) return [];
    const list = [...draft.warnings];
    if (profile) {
      const conflicts = findPreferenceConflicts(
        draft.ingredients.map((i) => i.name),
        {
          dietaryPreferences: profile.dietaryPreferences,
          avoidedIngredients: profile.avoidedIngredients,
        },
      );
      for (const c of conflicts) {
        const message = t('review.preferenceConflict', {
          label: c.label.replace(/_/g, ' '),
          ingredients: c.ingredients.join(', '),
        });
        if (!list.some((w) => w.code === 'CONFLICTS_WITH_PREFERENCES' && w.message === message))
          list.push({ code: 'CONFLICTS_WITH_PREFERENCES', severity: 'warning', message });
      }
    }
    return list;
  }, [draft, profile, t]);

  if (!draft || !initial) {
    return <p className="text-sm text-muted-foreground">{t('imports.job.noDraft')}</p>;
  }

  const originalImageUrl = draft.source?.originalImageUrl ?? draft.imageUrls[0] ?? null;

  const submit = (recipe: RecipeWriteInput) =>
    onConfirm({
      recipe,
      duplicateResolution: choice.resolution,
      existingRecipeId: choice.existingRecipeId,
      copyRemoteImage: copyImage,
    });

  return (
    <div className="space-y-6">
      <SourceBox source={draft.source} fallbackUrl={job.canonicalUrl ?? job.url} />
      {job.strategy === 'AI_FALLBACK' && (
        <p className="text-sm text-muted-foreground">{t('imports.job.aiFallbackNote')}</p>
      )}
      <WarningsList warnings={warnings} uncertainties={draft.uncertainties} />
      <DuplicatesPanel duplicates={job.duplicates} choice={choice} onChange={setChoice} />

      {originalImageUrl && (
        <section aria-labelledby="import-image" className="rounded-lg border p-3">
          <h2 id="import-image" className="mb-2 text-lg font-semibold">
            {t('imports.image.title')}
          </h2>
          <div className="flex flex-col gap-3 sm:flex-row">
            <img
              src={originalImageUrl}
              alt={t('imports.image.alt', { title: draft.title })}
              className="h-40 w-full rounded-md object-cover sm:w-56"
              loading="lazy"
              referrerPolicy="no-referrer"
            />
            <div className="space-y-2 text-sm">
              <p className="text-muted-foreground">{t('imports.image.remoteHint')}</p>
              <label className="flex min-h-[44px] items-start gap-3">
                <Checkbox
                  checked={copyImage}
                  onCheckedChange={(c) => setCopyImage(c === true)}
                  className="mt-0.5"
                  data-testid="copy-image-checkbox"
                />
                <span>
                  <span className="font-medium">{t('imports.image.copyLabel')}</span>
                  <br />
                  <span className="text-muted-foreground">{t('imports.image.copyRights')}</span>
                </span>
              </label>
            </div>
          </div>
        </section>
      )}

      <DraftReviewForm
        initial={initial}
        draft={draft}
        origin={draft.origin === 'AI_GENERATED' ? 'AI' : 'IMPORT'}
        submitLabel={t(`imports.duplicates.confirm.${choice.resolution}`)}
        submitting={confirming}
        onSubmit={submit}
      />
    </div>
  );
}
